import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../lib/adminApi";
import { AdminBar } from "./admin/AdminCore";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminAreas() {
  const [areas, setAreas] = useState([]);
  const [town, setTown] = useState("");
  const [county, setCounty] = useState("Surrey");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/areas`).then((r) => r.json()).then(setAreas).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Admin · Areas | Cedar Views";
    load();
  }, [load]);

  const addArea = async (e) => {
    e.preventDefault();
    if (!town.trim()) return;
    setBusy(true);
    try {
      const res = await authFetch("/api/areas", {
        method: "POST",
        body: JSON.stringify({ town: town.trim(), county, seo_description: desc.trim() || null }),
      });
      if (res.status === 409) { toast.error("That area already exists"); return; }
      if (!res.ok) throw new Error();
      toast.success(`${town.trim()} added`);
      setTown(""); setDesc("");
      load();
    } catch { toast.error("Could not add area"); }
    finally { setBusy(false); }
  };

  const removeArea = async (slug, name) => {
    if (!window.confirm(`Remove the ${name} page? This takes it offline immediately.`)) return;
    const res = await authFetch(`/api/areas/${slug}`, { method: "DELETE" });
    if (res.ok) { toast.success(`${name} removed`); load(); }
    else toast.error("Could not remove area");
  };

  const counties = ["Surrey", "Berkshire", "Hampshire"];

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-areas">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="Area Pages." />
        <p className="text-white/60 max-w-xl text-sm leading-relaxed">
          Add or remove local SEO area pages. New areas are created instantly with premium template copy;
          they appear on the <Link to="/areas/" className="underline text-white">Areas index</Link> and in search engines.
        </p>

        {/* ADD FORM */}
        <form onSubmit={addArea} className="mt-12 border border-white/12 p-8 grid grid-cols-1 md:grid-cols-4 gap-5 items-end" data-testid="admin-add-form">
          <div className="md:col-span-1">
            <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">Town *</label>
            <input value={town} onChange={(e) => setTown(e.target.value)} data-testid="admin-town-input" placeholder="e.g. Dorking"
              className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm" />
          </div>
          <div className="md:col-span-1">
            <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">County</label>
            <select value={county} onChange={(e) => setCounty(e.target.value)} data-testid="admin-county-select"
              className="w-full bg-[#050A15] border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm">
              {counties.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">Meta description (optional)</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="admin-desc-input" placeholder="Auto-generated if blank"
              className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm" />
          </div>
          <button type="submit" disabled={busy || !town.trim()} data-testid="admin-add-btn"
            className="btn-solid justify-center disabled:opacity-40">
            <Plus size={14} /> Add area
          </button>
        </form>

        {/* LIST */}
        <div className="mt-12 border border-white/12 divide-y divide-white/10" data-testid="admin-area-list">
          {areas.map((a) => (
            <div key={a.slug} className="flex items-center justify-between px-6 py-4 gap-4" data-testid={`admin-row-${a.slug}`}>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm">{a.town} <span className="text-white/40 font-normal">· {a.county}</span></p>
                <p className="font-mono-x text-[10px] text-white/35 mt-1 truncate">/areas/{a.slug}/</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Link to={`/areas/${a.slug}/`} data-testid={`admin-view-${a.slug}`}
                  className="p-2 text-white/50 hover:text-white transition-colors" title="View page">
                  <ExternalLink size={15} />
                </Link>
                <Link to={`/admin/areas/edit?slug=${a.slug}`} data-testid={`admin-edit-${a.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white hover:text-[#0B162C] text-xs font-semibold tracking-[0.12em] uppercase transition-colors">
                  <Pencil size={13} /> Edit
                </Link>
                <button onClick={() => removeArea(a.slug, a.town)} data-testid={`admin-delete-${a.slug}`}
                  className="p-2 text-white/50 hover:text-red-400 transition-colors" title="Remove page">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {areas.length === 0 && <p className="px-6 py-8 text-white/40 text-sm">No areas yet.</p>}
        </div>
        <p className="text-white/35 text-xs mt-6">{areas.length} live area pages</p>
      </div>
    </main>
  );
}
